import React from "react";
import { Link } from "react-router-dom";
import { SITE } from "../../config/site";

function ContactCard({ label, email, subject, description, tone = "brand" }) {
  const toneClass =
    tone === "cool"
      ? "text-[rgb(var(--cool))]"
      : "text-[rgb(var(--brand))]";

  return (
    <a
      href={`mailto:${email}?subject=${encodeURIComponent(subject)}`}
      className="
        group rounded-[2rem]
        border border-[rgb(var(--border))]
        bg-[rgb(var(--surface))]
        p-6
        shadow-[var(--shadow-soft)]
        transition
        hover:-translate-y-0.5
        hover:bg-[rgb(var(--elevated))]
      "
    >
      <div
        className={`
          text-xs font-black uppercase tracking-[0.16em]
          ${toneClass}
        `}
      >
        {label}
      </div>

      <h2
        className="
          mt-3 text-xl font-black
          tracking-[-0.03em]
          text-[rgb(var(--text-main))]
        "
      >
        {description}
      </h2>

      <div
        className="
          mt-6 flex items-center justify-between gap-4
          text-sm font-bold
          text-[rgb(var(--text-muted))]
        "
      >
        <span className="truncate">{email}</span>

        <span
          className={`
            shrink-0 transition-transform
            group-hover:translate-x-1
            ${toneClass}
          `}
        >
          →
        </span>
      </div>
    </a>
  );
}

function SocialLink({ href, icon, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="
        group flex items-center justify-between
        rounded-2xl
        border border-[rgb(var(--border))]
        bg-[rgb(var(--surface))]
        px-4 py-3.5
        transition
        hover:bg-[rgb(var(--elevated))]
      "
    >
      <div className="flex items-center gap-3">
        <div
          className="
            flex h-9 w-9 items-center justify-center
            rounded-xl
            bg-[rgb(var(--elevated))]
          "
        >
          <img
            src={icon}
            alt=""
            className="h-4 w-4"
            draggable="false"
          />
        </div>

        <span
          className="
            text-sm font-bold
            text-[rgb(var(--text-main))]
          "
        >
          {label}
        </span>
      </div>

      <span
        className="
          text-sm text-[rgb(var(--text-muted))]
          transition
          group-hover:text-[rgb(var(--text-main))]
        "
      >
        ↗
      </span>
    </a>
  );
}

export default function ContactPage() {
  return (
    <div
      className="
        min-h-screen
        bg-[rgb(var(--bg-app))]
        text-[rgb(var(--text-main))]
      "
    >
      <div
        className="
          h-1.5 w-full
          bg-[linear-gradient(90deg,rgb(var(--brand)),rgb(var(--cool)),rgb(var(--intent)))]
        "
      />

      <div
        className="
          mx-auto w-full max-w-4xl
          px-5 sm:px-7
        "
      >
        {/* Header */}
        <header
          className="
            flex items-center justify-between
            py-5 sm:py-6
          "
        >
          <Link
            to="/"
            className="flex items-center gap-3"
          >
            <div
              className="
                flex h-10 w-10 items-center justify-center
                rounded-2xl
                border border-[rgb(var(--border))]
                bg-[rgb(var(--surface))]
              "
            >
              <img
                src="/logo.svg"
                alt=""
                className="h-6 w-6"
              />
            </div>

            <span className="font-black">
              Veacon
            </span>
          </Link>

          <Link
            to="/"
            className="
              text-sm font-bold
              text-[rgb(var(--text-muted))]
              transition
              hover:text-[rgb(var(--text-main))]
            "
          >
            ← Home
          </Link>
        </header>

        <main>
          {/* Intro */}
          <section className="pb-10 pt-14 text-center sm:pt-20">
            <div
              className="
                text-xs font-black uppercase
                tracking-[0.18em]
                text-[rgb(var(--brand))]
              "
            >
              Contact
            </div>

            <h1
              className="
                mt-3
                text-4xl font-black
                tracking-[-0.05em]
                sm:text-5xl
              "
            >
              Get in touch.
            </h1>

            <p
              className="
                mt-3 text-sm
                text-[rgb(var(--text-muted))]
              "
            >
              
            </p>
          </section>

          {/* Contact */}
          <section className="grid gap-4 sm:grid-cols-2">
            <ContactCard
              label="Support"
              email={SITE.supportEmail}
              subject="Veacon Support"
              description="Account or app help"
              tone="brand"
            />

            <ContactCard
              label="General"
              email={SITE.helloEmail}
              subject="Veacon Contact"
              description="Questions, feedback, or partnerships"
              tone="cool"
            />
          </section>

          {/* Social */}
          <section
            className="
              mx-auto max-w-xl
              py-16 sm:py-20
            "
          >
            <div
              className="
                mb-5 text-center
                text-xs font-black uppercase
                tracking-[0.16em]
                text-[rgb(var(--text-muted))]
              "
            >
              Elsewhere
            </div>

            <div className="grid gap-3">
              <SocialLink
                href={SITE.social.instagram}
                icon="/instagram.svg"
                label="Instagram"
              />

              <SocialLink
                href={SITE.social.tiktok}
                icon="/tiktok.svg"
                label="TikTok"
              />

              <SocialLink
                href={SITE.social.discord}
                icon="/discord.svg"
                label="Discord"
              />
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer
          className="
            flex flex-col gap-4
            border-t border-[rgb(var(--border))]
            py-7
            text-sm
            sm:flex-row
            sm:items-center
            sm:justify-between
          "
        >
          <div
            className="
              font-bold
              text-[rgb(var(--text-muted))]
            "
          >
            @veacon.app
          </div>

          <div
            className="
              flex gap-5
              font-semibold
              text-[rgb(var(--text-muted))]
            "
          >
            <Link
              to="/privacy"
              className="hover:text-[rgb(var(--text-main))]"
            >
              Privacy
            </Link>

            <Link
              to="/terms"
              className="hover:text-[rgb(var(--text-main))]"
            >
              Terms
            </Link>
          </div>
        </footer>
      </div>
    </div>
  );
}